import type { Organization } from 'lib/types'
import { isVoiceEnabled } from './auth'

/**
 * The org whose Zello config an alert's voice channel runs on: the alert's own
 * org when it has voice, otherwise the nearest ancestor group that does. Voice
 * is often set up on a group while alerts fire from its properties.
 *
 * `loadOrg` resolves an org by id (cached or fetched); `parentOrgMap` backs up
 * `org.parents`, which is not populated on every org payload. Returns null when
 * nothing up the chain has voice, so callers leave the selection alone.
 */
export async function resolveVoiceOrgId(
  orgId: string | null | undefined,
  loadOrg: (id: string) => Promise<Organization | undefined>,
  parentOrgMap: { [childId: string]: string[] } = {},
): Promise<string | null> {
  const seen = new Set<string>()
  let queue: string[] = orgId ? [orgId] : []

  // Breadth-first, so the closest voice-enabled org wins over a grandparent.
  while (queue.length) {
    const next: string[] = []
    for (const id of queue) {
      if (seen.has(id)) continue
      seen.add(id)

      const org = await loadOrg(id).catch(() => undefined)
      if (!org) continue
      if (isVoiceEnabled(org)) return org.id

      next.push(...(org.parents?.length ? org.parents : parentOrgMap[id] || []))
    }
    queue = next
  }
  return null
}
